import React, { useEffect, useState, useMemo } from "react";
import { useUser } from "../../../../context/UserContext";
import { useAgreement } from "../../../../context/AgreementContext";
import { useSession } from "../../../../context/SessionContext";
import { formatDateStringNoDay, formatDuration } from "../../../../util_functions/helpers";
import {
  AiFillPlayCircle,
  AiOutlineQuestionCircle,
} from "react-icons/ai";

const ApproveStartComponent = () => {
  const { clientUser } = useUser();
  const userId = clientUser?.id;
  const userType = "client";

  const { agreements, listAgreementsByUser } = useAgreement();
  const { approveSessionStart, completeSession } = useSession();

  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!userId) return;
    listAgreementsByUser(userType, userId).catch(console.error);
  }, [listAgreementsByUser, userType, userId]);

  const pendingInfo = useMemo(() => {
    for (const ag of agreements || []) {
      const sessions = ag.agreementSessions || [];
      const req = sessions.find(
        s =>
          s.requestedStartAt &&
          !s.approvedStartAt &&
          s.status === "REQUESTED"
      );
      if (req) return { phase: "REQUESTED", agreement: ag, session: req };

      const appr = sessions.find(
        s => s.approvedStartAt && s.status === "APPROVED"
      );
      if (appr && ag.executionStage === "ACTIVE") {
        return { phase: "ACTIVE", agreement: ag, session: appr };
      }
    }
    return null;
  }, [agreements]);

  useEffect(() => {
    if (!pendingInfo || pendingInfo.phase !== "ACTIVE") {
      setElapsed(0);
      return;
    }
    const startTs = pendingInfo.session.approvedStartAt;
    const iv = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTs) / 1000));
    }, 1000);
    return () => clearInterval(iv);
  }, [pendingInfo]);

  const sessionInfos = useMemo(() => {
    const infos = [];
    for (const ag of agreements || []) {
      for (const s of ag.agreementSessions || []) {
        if (s.requestedStartAt && s.status === "REQUESTED") {
          infos.push({ phase: "REQUESTED", agreement: ag, session: s });
        } else if (
          s.approvedStartAt &&
          s.status === "APPROVED" &&
          ag.executionStage === "ACTIVE"
        ) {
          infos.push({ phase: "ACTIVE", agreement: ag, session: s });
        }
      }
    }
    infos.sort((a, b) => b.session.requestedStartAt - a.session.requestedStartAt);
    console.log("ApproveStartComponent sessions (sorted):", infos);
    return infos;
  }, [agreements]);

  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);

  if (sessionInfos.length === 0) return null;

  return (
    <>
      {sessionInfos.map(({ phase, agreement, session }) => {
        const isASAP = session.scheduledStart === 0;
        const displayTime = isASAP ? (
          <strong>As soon as possible</strong>
        ) : (
          formatDateStringNoDay(session.scheduledStart)
        );

        const elapsedSec =
          phase === "ACTIVE"
            ? Math.floor((now - session.approvedStartAt) / 1000)
            : 0;
        const elapsedLabel =
          phase === "ACTIVE" ? formatDuration(elapsedSec) : null;

        const handleApprove = async () => {
          try {
            await approveSessionStart(session.sessionId);
          } catch (err) {
            console.error("Approve failed:", err);
          }
        };

        const handleEnd = async () => {
          try {
            await completeSession(session.sessionId);
          } catch (err) {
            console.error("End failed:", err);
          }
        };

        const handleReject = () => {
          console.log("Rejected session start request:", session.sessionId);
        };

        return (
          <div key={session.sessionId} className="session-start-card">
            <h3>
              {phase === "ACTIVE" ? (
                <AiFillPlayCircle className="session-icon active-icon" />
              ) : (
                <AiOutlineQuestionCircle className="session-icon requested-icon" />
              )}{" "}
              {phase === "ACTIVE"
                ? "Session In Progress"
                : "Session Start Request"}
            </h3>

            <div className="card-body">
              <p>
                <span className="label">Worker</span>{" "}
                <strong>{agreement.workerName}</strong>
              </p>
              <p>
                <span className="label">Job</span> {agreement.jobTitle}
              </p>

              {phase !== "ACTIVE" && (
                <p>
                  <span className="label">When</span> {displayTime}
                </p>
              )}

              {phase === "ACTIVE" && (
                <>
                  <p>
                    <span className="label">Started</span>{" "}
                    {formatDateStringNoDay(session.approvedStartAt)}
                  </p>
                  <p>
                    <span className="label">Elapsed</span>{" "}
                    <strong>{elapsedLabel}</strong>
                  </p>
                </>
              )}
            </div>

            <div className="modal-actions">
              {phase === "REQUESTED" && (
                <>
                  <button className="btn approve" onClick={handleApprove}>
                    Approve Session Start
                  </button>
                  <button className="btn reject" onClick={handleReject}>
                    Reject Request
                  </button>
                </>
              )}

              {phase === "ACTIVE" && (
                <button className="btn end" onClick={handleEnd}>
                  End Session
                </button>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
};

export default ApproveStartComponent;
